import type {
  MetaArgs,
  MetaDescriptor,
} from '@remix-run/react/dist/routeModules'

import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.locale('ja')

type MetaProps = {
  pageTitle?: string
  description?: string
  image?: string
}

const PROJECT_YEAR = import.meta.env.VITE_PROJECT_YEAR
const TWO_DIGIT_YEAR = PROJECT_YEAR?.slice(2) ?? '00'

const defaultDescription = `シンアド就活${PROJECT_YEAR}は、広告代理店、PR会社、インターネット企業、優良ベンチャー企業など100社以上が集結する、業界最大規模の${TWO_DIGIT_YEAR}卒生向けの就活・就職支援サービスです。毎年20,000人を超える就活生が活用しています。`

export const getMultipleQueryParams = (
  urlSearchParams: URLSearchParams | null,
  key: string,
): string[] => {
  if (!urlSearchParams) return []

  return urlSearchParams
    .getAll(key)
    .flatMap((c) => c.split(','))
    .filter((c): c is string => c.trim() !== '')
}

const createInitialMeta = (
  title: string,
  description?: string,
): MetaDescriptor[] => {
  return [
    { title },
    {
      property: 'og:title',
      content: title,
    },
    {
      name: 'twitter:title',
      content: title,
    },
    {
      name: 'description',
      content: description
        ? `${description} | シンアド就活${PROJECT_YEAR}`
        : defaultDescription,
    },
    {
      property: 'og:description',
      content: description
        ? `${description} | シンアド就活${PROJECT_YEAR}`
        : defaultDescription,
    },
    {
      name: 'twitter:description',
      content: description
        ? `${description} | シンアド就活${PROJECT_YEAR}`
        : defaultDescription,
    },
  ]
}

const createBaseUrlMetaTags = (
  baseUrl: string,
  pathname: string,
  image?: string,
): MetaDescriptor[] => {
  const link = `${baseUrl}${pathname.slice(1)}`

  return [
    {
      tagName: 'link',
      rel: 'canonical',
      href: link,
    },
    {
      property: 'og:url',
      content: link,
    },
    {
      property: 'og:image',
      content: image ? image : `${baseUrl}/ogp-${PROJECT_YEAR}.png`,
    },
    {
      name: 'twitter:image',
      content: image ? image : `${baseUrl}/ogp-${PROJECT_YEAR}.png`,
    },
  ]
}

const createCanonicalMetaWithQueryParams = (
  meta: MetaDescriptor[],
  baseUrl: string | undefined,
  pathname: string,
  queryParams: Record<string, string | string[] | undefined> = {},
): MetaDescriptor[] => {
  if (!baseUrl) return meta

  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(queryParams)) {
    const params: string[] =
      typeof value === 'string' && value.trim() !== ''
        ? [value]
        : Array.isArray(value)
          ? value.filter((v): v is string => typeof v === 'string')
          : []

    if (params.length > 0) searchParams.set(key, params.join(','))
  }

  if (searchParams.toString() === '') return meta

  // 変数名をcanonicalUrlとしたいが、og:urlにも同じ値を用いるため汎用性のあるpageUrlと名付けた
  const pageUrl = `${baseUrl}${pathname.slice(1)}?${searchParams.toString()}`

  const filteredMeta = meta.filter((tag): tag is MetaDescriptor => {
    if ('rel' in tag && tag.rel === 'canonical') return false
    if ('property' in tag && tag.property === 'og:url') return false
    return true
  })

  filteredMeta.push(
    { tagName: 'link', rel: 'canonical', href: pageUrl },
    { property: 'og:url', content: pageUrl },
  )

  return filteredMeta
}

export const generateMetaDescriptor = (
  { location, matches }: MetaArgs,
  { pageTitle, description, image }: MetaProps,
  queryParams?: Record<string, string | string[] | undefined>,
): MetaDescriptor[] => {
  const rootMeta = matches.flatMap((match) => match.meta ?? [])
  const baseUrl = rootMeta.find(
    (meta): meta is MetaDescriptor & { href: string } =>
      'rel' in meta && meta.rel === 'canonical',
  )?.href

  const inheritedMeta = rootMeta
    .filter((meta): meta is MetaDescriptor => !('title' in meta))
    .filter((meta): meta is MetaDescriptor => {
      if (!('rel' in meta)) return true
      return meta.rel !== 'canonical'
    })

  const title = `${pageTitle ? `${pageTitle} | ` : ''}シンアド就活${PROJECT_YEAR} | 広告･PR･デジタル･IT業界の就活･就職サイト`

  const initialMeta: MetaDescriptor[] = [
    ...createInitialMeta(title, description),
    ...inheritedMeta,
  ]

  if (baseUrl) {
    initialMeta.push(
      ...createBaseUrlMetaTags(baseUrl, location.pathname, image),
    )
  }

  return createCanonicalMetaWithQueryParams(
    initialMeta,
    baseUrl,
    location.pathname,
    queryParams,
  )
}

export const passwordValidationRegex =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[A-Za-z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,}$/

export const nl2br = (text: string) =>
  text.replace('\r\n', '<br />').replace(/(\n|\r)/g, '<br />')

export const unique = <T>(array: T[]): T[] => [...new Set(array)]

export const createNumArray = (n: number) =>
  [...Array(n)].map((_: undefined, idx: number) => idx)

export const getLocation = (
  area: string | null | undefined,
  place: string | null | undefined,
): string | null => {
  return area ?? place ?? null
}

export const genYears = (
  sub = 0,
  add = 0,
  year: number = import.meta.env.VITE_GRADUATE_YEARS,
) => {
  const subYear = Array.from({ length: sub + 1 })
    .map((_, i) => year - i)
    .sort()
  const addYear = Array.from({ length: add + 1 }).map((_, i) => year + i)
  return unique([...subYear, year, ...addYear])
}

export const getRandomNumber = (base = 128, deviation = 20): number =>
  Math.floor(Math.random() * (2 * deviation + 1)) + (base - deviation)

export const sleep = (msec?: number) =>
  new Promise((resolve) => setTimeout(resolve, msec ?? 4000))

export const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const result: T[][] = []
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize))
  }
  return result
}

export const formatEventDate = (startAt: Date | null): string | null => {
  return startAt
    ? dayjs(startAt).tz('Asia/Tokyo').format('YYYY.MM.DD（ddd）')
    : null
}

export const formatEventTime = (
  startAt: Date | null,
  endAt: Date | null,
): string | null => {
  return startAt && endAt
    ? `${dayjs(startAt).tz('Asia/Tokyo').format('HH:mm')}〜${dayjs(endAt).tz('Asia/Tokyo').format('HH:mm')}`
    : null
}

export const calculateWeekDaysBefore = (
  date: Dayjs,
  daysBefore: number,
): Dayjs => {
  let count = 0
  let targetedDate = date

  while (count < daysBefore) {
    targetedDate = targetedDate.subtract(1, 'day')
    // 土日は除外
    if (targetedDate.day() !== 0 && targetedDate.day() !== 6) {
      count++
    }
  }
  return targetedDate
}

export const isValidDate = (date: Date | null | undefined): date is Date => {
  return date instanceof Date && !Number.isNaN(date.getTime())
}

/**
 * FormDataを再帰的にネストしたオブジェクトに変換する関数
 * sessions[0][id] → { sessions: [{ id: "..." }] } のような変換を行う
 * @param formData 変換するFormDataオブジェクト
 * @returns ネストしたオブジェクト
 */
export const formDataToNestedObject = (
  formData: FormData,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {}

  formData.forEach((value, key) => {
    if (value instanceof File) {
      result[key] = value
      return
    }

    // キーがネストした構造かどうかをチェック
    if (key.includes('[') && key.includes(']')) {
      // sessions[0][id] のような形式を解析
      const path = key.split(/[\[\]]+/).filter((v): v is string => Boolean(v))
      setNestedValue(result, path, value)
    } else {
      // 通常のキー
      result[key] = value
    }
  })

  return result
}

type Path = Record<string, unknown>

function setNestedValue(
  obj: Record<string, unknown>,
  path: string[],
  value: unknown,
): void {
  const processPath = (
    pathArray: string[],
  ): { key: string; index?: number }[] => {
    const result: { key: string; index?: number }[] = []
    let i = 0

    pathArray.forEach((segment, index) => {
      if (!Number.isNaN(Number(segment)) && i < pathArray.length - 1) {
        // 数値の場合は前の要素の配列インデックスとして扱う
        const lastItem = result[result.length - 1]
        if (lastItem) {
          lastItem.index = Number(segment)
        }
      } else {
        result.push({ key: segment })
        i = index
      }
    })

    return result
  }

  const processedPath = processPath(path)

  processedPath.reduce<Path>((current, { key, index }, pathIndex) => {
    if (pathIndex === processedPath.length - 1) {
      // 最後の要素の場合は値を設定
      if (typeof index === 'number') {
        // 配列の要素として設定
        if (!current[key]) current[key] = []
        ;(current[key] as unknown[])[index] = value
      } else {
        // オブジェクトのプロパティとして設定
        current[key] = value
      }
      return current
    }

    if (typeof index === 'number') {
      // 配列の作成と要素の初期化
      if (!current[key]) current[key] = []
      const currentArray = current[key] as unknown[]
      if (!currentArray[index]) currentArray[index] = {}
      return currentArray[index] as Path
    }

    // オブジェクトの作成
    if (!current[key]) current[key] = {}
    return current[key] as Path
  }, obj)
}
